import Activity from '@/models/_deprecated/Activity';
import Klass from '@/models/Klass';
import Lesson from '@/models/Lesson';
import { Nullable } from '@/models/Model';
import { LessonBlockRef } from '@/models/ScheduledLesson';
import User from '@/models/User';
import { ActivityTypeEnum } from './_deprecated/ActivityElement';
import {
  BlockCodeActivityElement,
  QuizActivityElement,
  QuizAnswersType,
  TextActivityElement,
  VideoActivityElement,
} from './_deprecated/ActivityElement';
import { DNDColumn } from './DNDColumn';
import { ColumnDND } from './DNDOrdenation';
import {
  CodeEditorActivity,
  LanguageCodeEditorEnum,
} from './CodeEditorActivity';

export default interface ActivityProgress {
  id: number;
  lessonProgress: number;
  activity: Activity;
  startDate?: string;
  lastUpdate?: string;
  hasDone: boolean;
  grade?: number;
  hasGrade?: boolean;
  answersRevealed: boolean;
  bestAttempt?: BestAttempt;
  currentQuestionOrder: number | null;
  questionsProgress?: (QuestionProgress | IQuestionProgress)[];
  lesson: Nullable<
    Pick<Lesson, 'id' | 'name'> & { order: Nullable<number> }
  >;
  lessonBlock: Nullable<Pick<LessonBlockRef, 'id' | 'order'>>;
  klass: Nullable<Pick<Klass, 'id' | 'name'>>;
  student: Nullable<Pick<User, 'id' | 'firstName' | 'lastName'>>;
  correctionStatus: ActivityCorrectionStatusEnum;
  sentAt: Nullable<string>;
}
export interface ActivityProgressShortView {
  id: number;
  activityName: string;
  activityId: number;
  lessonId: number;
  hasDone: boolean;
}

export enum ActivityCorrectionStatusEnum {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  PENDING = 'PENDING',
  CORRECTED = 'CORRECTED',
}

export type ActivityMode = 'activity' | 'review' | 'answerkey' | 'result';

export interface AttemptAnswer {
  answer: boolean | number | null;
  isCorrect: boolean;
  gradePercent?: number;
}

export type QuizActivityAttempt = {
  data: {
    quiz: QuizActivity;
    quizAnswers: QuizQuestion[];
  };
  type: ActivityTypeEnum.QUIZ;
  answers: {
    id: number;
    answer: string | null;
    isDone: boolean;
    isCorrect: boolean;
  };
};

export type CodeEditorActivityAttempt = {
  type: ActivityTypeEnum.CODE_EDITOR;
  data: CodeEditorActivity;
  progress: {
    timer: number;
    answer: string | null;
    isDoneone: boolean;
    isCorrect: boolean;
    details?: Record<string, unknown>;
  };
};

export type BlockCodeActivityAttempt = {
  type: ActivityTypeEnum.BLOCK;
  data: BlockCodeActivityElement;
  progress: {
    timer: number;
    answer: string | null;
    answerWorkspace: object | null;
    isDone: boolean;
    isCorrect: boolean;
    details?: Record<string, unknown>;
  };
};

// TODO: adicionar as demais questões aqui
type QuestionContent =
  | QuizActivityAttempt
  | CodeEditorActivityAttempt
  | BlockCodeActivityAttempt;

export type AttemptContent = {
  order: number;
} & (
  | QuestionContent
  | {
      type: ActivityTypeEnum.TEXT | ActivityTypeEnum.VIDEO;
    }
);

export interface ActivityProgressAttempt {
  id: number;
  activityProgress: number;
  attemptNumber: number;
  content: AttemptContent[] | null;
  correctQuestions: number;
  evaluativeQuestions: number;
  grade: number;
}

export type IQuestionProgress = {
  answer?: any;
  progressId: number;
  questionId: number;
  type: ActivityTypeEnum;
  order: number;
  isDone: boolean;
  isCurrentQuestion: boolean;
};

export type BaseQuestionTypeProgress = {
  id: number;
  isDone: boolean;
  isCurrentQuestion: boolean;
};

export class QuestionProgress {
  answer?: any;
  progressId!: number;
  questionId!: number;
  type!: ActivityTypeEnum;
  order!: number;
  isDone!: boolean;
  isCurrentQuestion!: boolean;

  constructor(questionProgress?: IQuestionProgress) {
    Object.assign(this, questionProgress);
  }
}

export interface BestAttempt {
  id: number;
  activityProgress: number;
  grade: number;
  attemptNumber: number;
}

export interface QuizQuestion {
  id: number;
  answerVerifier: string;
  answer: string;
  altText?: string;
}

export interface QuizActivity extends QuizActivityElement {
  quizAnswers: QuizQuestion[];
}

export interface QuizActivityProgress extends BaseQuestionTypeProgress {
  quizActivity: QuizActivity;
  student: number;
  answer: string;
  isDone: boolean;
}

export interface ActivityCorrectAnswers {
  id: number;
  name: string;
  questions?: ActivityCorrectAnswersQuestion[];
}

interface ActivityCorrectAnswersQuestion {
  id: number;
  order: number;
  type: string;
  reason: string;
}

export interface QuizCorrectAnswer extends ActivityCorrectAnswersQuestion {
  correctAnswer: QuizQuestion;
  correctAnswerIndex: number;
  answerType: QuizAnswersType;
}

export interface TrueOrFalseCorrectAnswer
  extends ActivityCorrectAnswersQuestion {
  isTrue: boolean;
}

export interface BlockCodeCorrectAnswer extends ActivityCorrectAnswersQuestion {
  solutionCode: string;
  solutionWorkspace: object;
}

export interface CodeEditorCorrectAnswer
  extends ActivityCorrectAnswersQuestion {
  solutionCode: string;
  language: LanguageCodeEditorEnum;
}

export interface DNDOrdenationCorrectAnswer
  extends ActivityCorrectAnswersQuestion {
  title: string;
  subtitle: string;
  columns: ColumnDND[];
}

export interface DNDPositionalCorrectAnswer
  extends ActivityCorrectAnswersQuestion {
  title: string;
  subtitle: string;
  questionColumn: DNDColumn;
  answerColumn: DNDColumn;
}

export interface TextActivityProgress extends BaseQuestionTypeProgress {
  id: number;
  textActivity: TextActivityElement;
  student: number;
  timer: number;
  isDone: boolean;
}

export interface VideoActivityProgress extends BaseQuestionTypeProgress {
  id: number;
  videoActivity: VideoActivityElement;
  student: number;
  timer: number;
  isDone: boolean;
}

export interface TrueOrFalseActivity {
  id: number;
  question: string;
  content?: string;
  order: number;
}

export interface TrueOrFalseActivityProgress extends BaseQuestionTypeProgress {
  id: number;
  trueOrFalseActivity: TrueOrFalseActivity;
  student: number;
  questionId: number;
  questionType: ActivityTypeEnum;
  questionOrder: number;
  answer: boolean;
  isDone: boolean;
}

export type CodeEditorActivityProgress = BaseQuestionTypeProgress & {
  codeEditorActivity: CodeEditorActivity;
  timer: number;
  answer: string | null;
};

export type BlockCodeActivityProgress = BaseQuestionTypeProgress & {
  blockActivity: BlockCodeActivityElement;
  timer: number;
  answer: string | null;
  answerWorkspace: object | null;
};
